import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { RmsModule } from '@rms';
import { WebService, PromptService, RmsMessageService, lastValueFrom } from '@services';
import { Column } from '@interfaces';

@Component({
  selector: 'app-cost-adjustments',
  imports: [RmsModule],
  templateUrl: './cost-adjustments.component.html',
  styleUrl: './cost-adjustments.component.scss'
})
export class CostAdjustmentsComponent implements OnInit {
  @Input() InventoryID: number | null = null;
  @Output() adjustmentSaved = new EventEmitter<void>();
  DataLoaded: boolean = false;
  Adjustments: any[] = [];
  AdjustmentColumns: Column[] = [];
  Reasons: { ReasonID: number, ReasonName: string }[] = [];

  constructor(private webService: WebService, private prompt: PromptService, private message: RmsMessageService) { }

  async ngOnInit() {
    await this.GetAdjustments();
    await this.GetReasons();
    this.DataLoaded = true;
  }

  async GetAdjustments() {
    this.Adjustments = await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/inventory/cost-adjustment/history', { params: [{ name: 'InventoryID', value: this.InventoryID }] }));
    this.AdjustmentColumns = [
      { label: 'Date', key: 'DateAdjusted', type: 'date', width: 15 },
      { label: 'Reason', key: 'ReasonName', width: 20 },
      { label: 'Amount', key: 'AdjustmentAmount', type: 'currency', width: 15 },
      { label: 'Notes', key: 'Notes', width: 35 },
      { label: 'Adjusted By', key: 'AdjustedByName', width: 15 }
    ]
  }

  async GetReasons() {
    this.Reasons = await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/inventory/cost-adjustment/reasons'));
  }

  onClickAddAdjustment() {
    const reasonPrompt = this.prompt.open({
      promptText: 'What is this adjustment for?',
      inputRequired: true,
      promptInput: 'select',
      inputLabel: 'Reason',
      inputOptions: this.Reasons.map(r => ({ value: r.ReasonID, label: r.ReasonName })),
      buttons: [{ label: 'Next', value: true, buttonClass: 'btn-bg-green' }]
    });

    reasonPrompt.closed$.subscribe(async (reasonResult: any) => {
      if (!reasonResult || !reasonResult.inputValue) {
        this.message.error('Adjustment cancelled: no reason selected.');
        return;
      }
      const reasonId = Number(reasonResult.inputValue);

      const amountPrompt = this.prompt.open({
        promptText: 'Enter the adjustment to the cost per pound ($/lb). Positive amounts add to the cost; negative amounts reduce it.',
        inputRequired: true,
        promptInput: 'text',
        inputLabel: 'Adjustment ($/lb)',
        buttons: [{ label: 'Next', value: true, buttonClass: 'btn-bg-green' }]
      });

      amountPrompt.closed$.subscribe(async (amountResult: any) => {
        const amount = Number(amountResult?.inputValue);
        if (!amountResult || isNaN(amount) || amount === 0) {
          this.message.error('Adjustment cancelled: enter a valid amount.');
          return;
        }

        const notesPrompt = this.prompt.open({
          promptText: 'Please explain the reason for this adjustment',
          inputRequired: true,
          promptInput: 'textarea',
          inputLabel: 'Notes (Required)',
          buttons: [{ label: 'Save', value: true, buttonClass: 'btn-bg-green' }]
        });

        notesPrompt.closed$.subscribe(async (notesResult: any) => {
          if (!notesResult || !notesResult.inputValue) {
            this.message.error('Adjustment cancelled: notes are required.');
            return;
          }
          await this.SaveAdjustment(reasonId, amount, notesResult.inputValue);
        });
      });
    });
  }

  async SaveAdjustment(ReasonID: number, AdjustmentAmount: number, Notes: string) {
    const body = {
      InventoryID: this.InventoryID,
      ReasonID: ReasonID,
      AdjustmentAmount: AdjustmentAmount,
      Notes: Notes,
      AdjustedByGUID: localStorage.getItem('GUID')
    }
    try {
      await lastValueFrom(this.webService.getWebCall('inventory', '/coffee/inventory/cost-adjustment/create', { body: body }));
      this.message.success('Successfully added cost adjustment.');
      await this.GetAdjustments();
      this.adjustmentSaved.emit();
    }
    catch (err) {
      this.message.error('Error saving adjustment: ' + err);
    }
  }
}
